import React, { useState } from "react";
import { MdSettings, MdRefresh } from "react-icons/md";
import { useTaskList } from "../../dashboard/store/useTaskList";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/Empty";
import { CardSkeleton } from "@/components/ui/Skeleton";
import { useAlgorithmConfig } from "../store/useAlgoritmh";

const AlgorithmConfigPage: React.FC = () => {
  const { tasks, loading: tasksLoading } = useTaskList();
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const {
    config,
    loading: configLoading,
    error,
    refetch,
  } = useAlgorithmConfig(selectedTaskId);

  return (
    <div className="space-y-4">
      {/* Task selector */}
      <Card
        title="Select Task"
        subtitle="Choose a task to view its algorithm configuration"
      >
        <div className="flex gap-3 flex-wrap">
          <select
            value={selectedTaskId}
            onChange={(e) => setSelectedTaskId(e.target.value)}
            disabled={tasksLoading}
            className="flex-1 min-w-48 border border-gray-200 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent bg-white"
          >
            <option value="">— Select a task —</option>
            {tasks.map((t) => (
              <option key={t.taskId} value={t.taskId}>
                {t.deviceName} ({t.taskId})
              </option>
            ))}
          </select>
          {selectedTaskId && (
            <button
              onClick={refetch}
              className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <MdRefresh size={15} />
              Refresh
            </button>
          )}
        </div>
      </Card>

      {/* Config display */}
      {!selectedTaskId ? (
        <EmptyState
          title="No task selected"
          description="Select a task above to view its algorithm configuration."
        />
      ) : configLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : error ? (
        <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-lg border border-red-200">
          {error}
        </div>
      ) : config ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Device Info */}
          <Card
            title="Device Information"
            subtitle="Camera / stream configuration"
            headerRight={
              <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center">
                <MdSettings size={16} className="text-indigo-600" />
              </div>
            }
          >
            <dl className="space-y-2.5">
              {[
                ["Name", config.device.name],
                ["Status", config.device.status],
                ["Type", config.device.type],
                ["Codec", config.device.codec],
                ["Resolution", config.device.resolution],
                ["Protocol", config.device.protocol],
                ["Stream URL", config.device.url],
                ["Media Server", config.device.mediaServer],
              ].map(([label, value]) => (
                <div key={label} className="flex gap-2">
                  <dt className="text-xs text-gray-400 w-28 shrink-0">
                    {label}
                  </dt>
                  <dd className="text-xs font-medium text-gray-700 break-all">
                    {String(value)}
                  </dd>
                </div>
              ))}
            </dl>
          </Card>

          {/* Algorithm Info */}
          <Card
            title="Algorithms"
            subtitle={`${config.algorithms.length} algorithm(s) configured`}
          >
            {config.algorithms.length === 0 ? (
              <EmptyState title="No algorithms configured" />
            ) : (
              <div className="space-y-3">
                {config.algorithms.map((algo, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50 rounded-lg p-3 text-xs  text-gray-600 overflow-auto max-h-48"
                  >
                    <pre>{JSON.stringify(algo, null, 2)}</pre>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      ) : null}
    </div>
  );
};

export default AlgorithmConfigPage;
